import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IAutoCompleteScrollEvent {
  autoComplete: MatAutocomplete;
  scrollEvent: Event;
}

@Directive({
  selector: 'mat-autocomplete[optionsScroll]',
  exportAs: 'mat-autocomplete[optionsScroll]',
  standalone: false,
})
export class MatAutocompleteOptionsScrollDirective {
  @Input() thresholdPercent = 0.8;
  @Output('optionsScroll') scroll = new EventEmitter<boolean>();
  private readonly onDestroy = new Subject<void>();
  private readonly onPanelScroll = (event: Event): void => {
    this.emitWhenNearBottom(event);
  };

  constructor(public autoComplete: MatAutocomplete) {
    this.autoComplete.opened.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      setTimeout(() => this.attachScrollListener());
    });

    this.autoComplete.closed.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.removeScrollEventListener();
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
    this.removeScrollEventListener();
  }

  private attachScrollListener(): void {
    const panel = this.autoComplete.panel?.nativeElement as HTMLElement | undefined;
    if (!panel) {
      return;
    }
    this.removeScrollEventListener();
    panel.addEventListener('scroll', this.onPanelScroll);
  }

  private removeScrollEventListener(): void {
    const panel = this.autoComplete.panel?.nativeElement as HTMLElement | undefined;
    panel?.removeEventListener('scroll', this.onPanelScroll);
  }

  private emitWhenNearBottom(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollHeight <= clientHeight + 1) {
      return;
    }
    const distanceToBottom = scrollHeight - target.scrollTop - clientHeight;
    const threshold = scrollHeight * (1 - this.thresholdPercent);
    if (distanceToBottom <= threshold) {
      this.scroll.next(true);
    }
  }
}
